import { exportToBlob } from 'tldraw';
import { extractBoardDocumentSnapshot } from './boardSnapshot';
import { clampRecentIntent } from './boardRecentIntent';
import { readBoardGoal, readCurrentStep } from './boardGoalFields';
import { useBoardStore } from '../store/useBoardStore';
import { collectVisiblePinIds } from './visiblePinIds';

export { collectVisiblePinIds, VISIBLE_PIN_IDS_MAX } from './visiblePinIds';

const DRAW_COMMAND_PATTERN = /\b(drawing|sketch|drew|diagram|what i drew|my drawing)\b/i;

function cameraToViewport(editor) {
  const cam = editor.getCamera();
  return { x: cam.x, y: cam.y, zoom: cam.z };
}

const AI_PIN_SHAPE_TYPES = new Set(['suggest-pin', 'conversation-pin']);

function countAiPins(editor) {
  if (!editor?.getCurrentPageShapes) return 0;
  let count = 0;
  for (const shape of editor.getCurrentPageShapes()) {
    if (AI_PIN_SHAPE_TYPES.has(shape?.type)) count += 1;
  }
  return count;
}

function countFrames(editor, board) {
  const fromBoard = Array.isArray(board?.frames) ? board.frames.length : 0;
  if (!editor?.getCurrentPageShapes) return fromBoard;
  let count = 0;
  for (const shape of editor.getCurrentPageShapes()) {
    if (shape?.type === 'frame') count += 1;
  }
  return Math.max(count, fromBoard);
}

export function collectSelection(editor) {
  const pinIds = [];
  const shapeIds = [];

  if (!editor?.getSelectedShapeIds) {
    return { pinIds, shapeIds };
  }

  for (const shapeId of editor.getSelectedShapeIds()) {
    const shape = editor.getShape(shapeId);
    if (!shape) continue;
    if (shape.type === 'pin-card' && shape.meta?.activityId) {
      pinIds.push(String(shape.meta.activityId));
    } else {
      shapeIds.push(String(shapeId));
    }
  }

  return { pinIds, shapeIds };
}

async function captureViewportScreenshot(editor) {
  try {
    const ids = editor.getCurrentPageShapeIds();
    const blob = await exportToBlob({
      editor,
      ids: [...ids],
      format: 'png',
      opts: { background: true, scale: 0.5 },
    });
    if (!blob) return null;
    const buffer = await blob.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i += 1) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  } catch {
    return null;
  }
}

export async function buildClientContext(editor, options = {}) {
  const context = await buildClientContextWithDelta(editor, options);
  if (context) return context;
  const { board } = options;
  const fallback = {
    viewport: board?.viewport || { x: 0, y: 0, zoom: 1 },
    selection: { pinIds: [], shapeIds: [] },
  };
  const withdrawnConstraints = compactWithdrawnConstraints(
    board?.contextRecommendations?.constraints
  );
  if (withdrawnConstraints.length) fallback.withdrawnConstraints = withdrawnConstraints;
  if (board?.contextRecommendations?.pendingDigest?.deletedSinceStepRefresh) {
    fallback.stepStaleAfterDelete = true;
  }
  return fallback;
}

function compactWithdrawnConstraints(constraints = []) {
  return (Array.isArray(constraints) ? constraints : [])
    .filter(item => item && item.active === false && item.withdrawn)
    .slice(-6)
    .map(item => ({
      id: String(item.id || '').slice(0, 40),
      kind: String(item.kind || '').slice(0, 20),
      slot: String(item.slot || '').slice(0, 40),
      claim: String(item.claim || '').slice(0, 160),
      value: String(item.value || '').slice(0, 80),
      active: false,
      withdrawn: true,
    }))
    .filter(item => item.claim || item.value);
}

function toSelectionKey(selection) {
  const pinIds = [...(selection?.pinIds || [])].sort().join(',');
  const shapeIds = [...(selection?.shapeIds || [])].sort().join(',');
  return `${pinIds}|${shapeIds}`;
}

function buildDeltaOps({
  previousContext,
  nextContext,
  board,
  pinCount,
  addedPinActivityId,
  recentIntent = '',
}) {
  if (!previousContext) {
    return [{ type: 'context_init' }];
  }

  const ops = [];
  if (toSelectionKey(previousContext.selection) !== toSelectionKey(nextContext.selection)) {
    ops.push({ type: 'selection_changed' });
  }

  const prevViewport = previousContext.viewport || {};
  const nextViewport = nextContext.viewport || {};
  const viewportShift = Math.abs((prevViewport.x || 0) - (nextViewport.x || 0))
    + Math.abs((prevViewport.y || 0) - (nextViewport.y || 0));
  const zoomShift = Math.abs((prevViewport.zoom || 1) - (nextViewport.zoom || 1));
  if (viewportShift > 120 || zoomShift > 0.12) {
    ops.push({ type: 'viewport_changed' });
  }

  if ((previousContext.pinCount || 0) !== (pinCount || 0)) {
    const isAdd = (previousContext.pinCount || 0) < (pinCount || 0);
    const op = { type: isAdd ? 'pin_added' : 'pin_removed' };
    if (isAdd && addedPinActivityId) {
      op.payload = { activityId: String(addedPinActivityId) };
    }
    ops.push(op);
  }

  if ((previousContext.aiPinCount || 0) !== (nextContext.aiPinCount || 0)) {
    const isAdd = (previousContext.aiPinCount || 0) < (nextContext.aiPinCount || 0);
    ops.push({ type: isAdd ? 'ai_pin_added' : 'ai_pin_removed' });
  }

  if ((previousContext.currentStep || previousContext.goal || '') !== readCurrentStep(board)) {
    ops.push({ type: 'goal_changed' });
  }

  if ((previousContext.boardGoal || previousContext.description || '') !== readBoardGoal(board)) {
    ops.push({ type: 'description_changed' });
    ops.push({ type: 'board_goal_changed' });
  }

  if (clampRecentIntent(previousContext.recentIntent) !== clampRecentIntent(recentIntent)) {
    ops.push({ type: 'intent_changed' });
  }

  if (String(previousContext.focusedPinId || '') !== String(nextContext.focusedPinId || '')) {
    ops.push({ type: nextContext.focusedPinId ? 'focus_changed' : 'focus_cleared' });
  }

  return ops;
}

export async function buildClientContextWithDelta(
  editor,
  {
    command,
    board,
    previousContext = null,
    pinCount = 0,
    addedPinActivityId = null,
    includeSnapshot = true,
    recentIntent = null,
    locale = 'en',
  } = {}
) {
  if (!editor) {
    return null;
  }

  const resolvedRecentIntent = clampRecentIntent(
    recentIntent != null ? recentIntent : (board?.recentIntent || '')
  );

  const currentStep = readCurrentStep(board);
  const boardGoal = readBoardGoal(board);
  const selection = collectSelection(editor);
  const visiblePinIds = collectVisiblePinIds(editor);
  const storedFocus = String(useBoardStore.getState().focusedPinId || '').trim();
  const focusedPinId = storedFocus || selection.pinIds[0] || '';

  const context = {
    viewport: cameraToViewport(editor),
    selection,
    focusedPinId: focusedPinId || null,
    visiblePinIds,
    pinCount,
    aiPinCount: countAiPins(editor),
    frameCount: countFrames(editor, board),
    currentStep,
    boardGoal,
    goal: currentStep,
    description: boardGoal,
    recentIntent: resolvedRecentIntent,
    locale: String(locale || 'en'),
  };
  const withdrawnConstraints = compactWithdrawnConstraints(
    board?.contextRecommendations?.constraints
  );
  if (withdrawnConstraints.length) context.withdrawnConstraints = withdrawnConstraints;
  if (board?.contextRecommendations?.pendingDigest?.deletedSinceStepRefresh) {
    context.stepStaleAfterDelete = true;
  }
  if (includeSnapshot) {
    context.liveSnapshot = extractBoardDocumentSnapshot(editor);
  }
  context.deltaOps = buildDeltaOps({
    previousContext,
    nextContext: context,
    board,
    pinCount,
    addedPinActivityId,
    recentIntent: resolvedRecentIntent,
  });

  if (command && DRAW_COMMAND_PATTERN.test(command)) {
    const screenshot = await captureViewportScreenshot(editor);
    if (screenshot) {
      context.viewportScreenshot = screenshot;
    }
  }

  return context;
}
